import http, { fetchWithAuth } from '../../../lib/http/httpClient';
import { Conversation, Message } from '../types';

export interface SendChatMessageParams {
  message: string;
  conversation_id?: string | null;
  cust_id: string;
}

export interface ConversationHistoryResponse {
  conversation_id: string;
  messages: Message[];
}

export const getConversations = async (custId: string, limit: number = 100): Promise<Conversation[]> => {
  const data = await http.get<Conversation[]>(`/api/v1/cp/chat/conversations?cust_id=${encodeURIComponent(custId)}&limit=${limit}`);
  return data || [];
};

export const fetchConversations = getConversations;

export const getConversationHistory = async (conversationId: string, custId: string): Promise<ConversationHistoryResponse> => {
  const data = await http.get<{ conversation_id: string; messages: any[] }>(`/api/v1/cp/chat/history/${encodeURIComponent(conversationId)}?cust_id=${encodeURIComponent(custId)}`);

  const rawMessages: any[] = data?.messages || [];
  const messages: Message[] = rawMessages.map((m) => ({
    id: m.message_id || String(Math.random()),
    role: m.role === 'user' ? 'user' : 'assistant',
    content: m.content || '',
    blocks: m.blocks || [],
    created_at: m.created_at,
  }));

  return {
    conversation_id: data.conversation_id,
    messages,
  };
};

export const fetchConversationHistory = getConversationHistory;

export const deleteConversation = async (conversationId: string, custId: string): Promise<void> => {
  return http.delete(`/api/v1/cp/chat/history/${encodeURIComponent(conversationId)}?cust_id=${encodeURIComponent(custId)}`);
};

export const renameConversation = (convId: string, custId: string, title: string) => {
  return http.patch<{ conversation_id: string; title: string }>(
        `/api/v1/cp/chat/conversations/${encodeURIComponent(convId)}?cust_id=${encodeURIComponent(custId)}`,
        { title }
    );
}

export const sendChatMessageStream = async (params: SendChatMessageParams, signal?: AbortSignal): Promise<Response> => {
  return fetchWithAuth('/api/v1/cp/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: {
      message: params.message,
      conversation_id: params.conversation_id,
      cust_id: params.cust_id,
    },
    signal,
  });
};

export const downloadChatExport = async (url: string): Promise<{ blob: Blob; filename: string }> => {
  const res = await fetchWithAuth(url, {
    method: 'GET',
  });

  if (!res.ok) {
    if (res.status === 404) {
      throw new Error('This export has expired. Ask MRC AI to export again.');
    }
    if (res.status === 403) {
      throw new Error('You no longer have access to this export.');
    }
    const text = await res.text().catch(() => '');
    throw new Error(`Export download failed: ${res.status} ${text || res.statusText}`);
  }

  const disposition = res.headers.get('content-disposition') || '';
  let filename = 'shipments-export.xlsx';
  const match = disposition.match(/filename="?([^";\n]+)"?/i);
  if (match && match[1]) {
    filename = match[1];
  }

  const blob = await res.blob();
  return { blob, filename };
};
